import { useEffect, useRef, useState } from "react";
import type { ComplaintItemViewModel } from "./ViewModel";
import { FormProvider, useForm } from "react-hook-form";
import getClassMetadata from "../../Common/classMetadataService";
import getComplaintTypes from "./getComplaintTypes";
import LoadingContentWrapper from "../../Common/Loading/LoadingContentWrapper";
import FormButtons from "../../Common/EditForm/formButtons";
import getTextMetadata from "../../Common/EditForm/ClassMetadata/getTextMetadata";
import SelectFormRow from "../../Common/EditForm/Select/SelectFormRow";
import DecimalInputFormRow from "../../Common/EditForm/DecimalInput/DecimalInputReactNumberFormatFormRow";
import getDecimalMetadata from "../../Common/EditForm/ClassMetadata/getDecimalMetadata";
import TextAreaSection from "../../Common/EditForm/TextArea/TextAreaSection";
import getTextAreaMetadata from "../../Common/EditForm/ClassMetadata/getTextAreaMetadata";
import BusinessDocumentItemSelectorFragment, {
  type BusinessDocumentItemSelectorFragmentHandle,
} from "../../BusinessDocuments/BusinessDocumentItemSelector/BusinessDocumentItemSelectorFragment";
import { v4 as uuidv4 } from "uuid";

export const complaintReasonId_Other = 5;
export const complaintReasonId_TransportDamage = 6;

export default function ComplaintItemForm(props: {
  values: ComplaintItemViewModel | null;
  onOk: (values: ComplaintItemViewModel) => void;
  onCancel: () => void;
  businessDocumentType: number;
  documentNumber: string;
}) {
  const vmDefaultValues: ComplaintItemViewModel = {
    id: uuidv4(),
    documentItem: null,
    amount: null,
    complaintTypeId: null,
    comments: "",
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isEditForm: boolean = !!props.values; // helper to determine if this is a new or edit form
  const [metadata, setMetadata] = useState<{ [key: string]: any }>({});
  const [complaintTypes, setComplaintTypes] = useState<
    {
      value: number;
      label: string;
    }[]
  >([]);
  const [isReasonOther, setIsReasonOther] = useState<boolean>(false);
  const [isReasonTransportDamage, setIsReasonTransportDamage] =
    useState<boolean>(false);

  const methods = useForm<ComplaintItemViewModel>({
    mode: "onTouched",
    defaultValues: vmDefaultValues,
  });

  const { handleSubmit, watch, trigger, formState, control, getValues, reset } =
    methods;

  const { isSubmitting } = formState;

  const onSubmit = async (values: ComplaintItemViewModel) => {
    // let vmToSave = Object.assign({}, viewModel, values); // if values contain all the necessary data, this step will be unnecessary
    props.onOk(values);
    return;
  };

  const onInitialize = async () => {
    const retrievedComplaintTypes = await getComplaintTypes();
    const valueLabelPairs = retrievedComplaintTypes.map((x) => {
      return { value: x.id, label: x.name };
    });
    setComplaintTypes(valueLabelPairs);

    const metadata_response = await getClassMetadata("ComplaintItemVM");
    setMetadata(metadata_response);

    if (isEditForm) {
      reset(props.values!);
    } else {
      reset(vmDefaultValues);
    }

    setIsLoading(false);
  };

  const businessDocumentItemSelectorRef =
    useRef<BusinessDocumentItemSelectorFragmentHandle>(null);

  useEffect(() => {
    onInitialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // we want to trigger revalidation of catalogNumber on every change of lineCode
  // we want to show an info box when the reason is other or transport damage
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === "complaintTypeId") {
        setIsReasonOther(value.complaintTypeId === complaintReasonId_Other);

        if (
          isEditForm ||
          formState.touchedFields.comments ||
          formState.isSubmitted
        ) {
          trigger("comments");
        }

        setIsReasonTransportDamage(
          value.complaintTypeId === complaintReasonId_TransportDamage
        );
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, formState]);

  return (
    <>
      <div className="content-wrapper">
        <h1 className="border-bottom pb-2 mt-4 mb-5">
          {" "}
          {isEditForm
            ? "Edycja pozycji reklamacji "
            : "Nowa pozycja reklamacji"}
        </h1>
        <LoadingContentWrapper isLoading={isLoading}>
          {!isLoading && (
            <FormProvider {...methods}>
              <form noValidate>
                <BusinessDocumentItemSelectorFragment
                  label="Nazwa towaru *"
                  name="documentItem"
                  id="documentItem"
                  control={control}
                  required={true}
                  ref={businessDocumentItemSelectorRef}
                  businessDocumentType={props.businessDocumentType}
                  documentNumber={props.documentNumber}
                />

                <DecimalInputFormRow
                  {...getDecimalMetadata<ComplaintItemViewModel>(
                    "amount",
                    metadata["amount"]
                  )}
                  label="Ilość do reklamacji *"
                  longDescription="Ilość wadliwych sztuk towaru (wymagane)."
                  control={control}
                  inlineDescription={undefined}
                ></DecimalInputFormRow>

                <SelectFormRow
                  {...getTextMetadata<ComplaintItemViewModel>(
                    "complaintTypeId",
                    metadata["complaintTypeId"]
                  )}
                  label="Rodzaj reklamacji *"
                  longDescription="Przyczyna reklamacji towaru (wymagane)."
                  control={control}
                  inlineDescription={undefined}
                  options={complaintTypes}
                  addNullOption={true}
                  valueIsNumber={true}
                  className="-app-medium-control"
                />

                {isReasonOther && (
                  <div className="row">
                    <div className="col-sm-8 offset-sm-4">
                      <div
                        className="alert alert-primary d-flex align-items-center"
                        role="alert"
                      >
                        <div className="flex-shrink-0 ms-3 me-4">
                          <i className="bi bi-lightbulb"></i>
                        </div>
                        <div>
                          Wybrałeś rodzaj reklamacji <b>Inne</b>. W polu{" "}
                          <b>Uwagi</b> poniżej opisz przyczynę.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isReasonTransportDamage && (
                  <div className="row">
                    <div className="col-sm-8 offset-sm-4">
                      <div
                        className="alert alert-primary d-flex align-items-center"
                        role="alert"
                      >
                        <div className="flex-shrink-0 ms-3 me-4">
                          <i className="bi bi-lightbulb"></i>
                        </div>
                        <div>
                          Wybrałeś rodzaj reklamacji <b>Szkoda transportowa</b>.
                          Przed wysłaniem upewnij się, że na głównej stronie
                          formularza dodałeś załącznik z protokołem szkody.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <hr />
                <TextAreaSection
                  {...getTextAreaMetadata<ComplaintItemViewModel>(
                    "comments",
                    metadata["comments"]
                  )}
                  rows={6}
                  label="Uwagi *"
                  longDescription='Uwagi do reklamowanego towaru (wymagane tylko dla przyczyny reklamacji "Inne")'
                  control={control}
                  inlineDescription={undefined}
                  required={false}
                  rules={{
                    validate: {
                      requiredWhenReasonIsOther: (v: any) => {
                        const hasComments = !!v && !!v.trim();
                        const isReasonOtherLocal =
                          getValues("complaintTypeId") ===
                          complaintReasonId_Other;
                        return isReasonOtherLocal && !hasComments
                          ? 'Dla przyczyny reklamacji "Inne" pole jest wymagane.'
                          : undefined;
                      },
                    },
                  }}
                />

                <div className="row pt-2">
                  <div className="offset-sm-4 col-sm-8">
                    <FormButtons
                      onSave={(e) => handleSubmit(onSubmit)(e)}
                      onCancel={() => props.onCancel()}
                      isSubmitting={isSubmitting}
                    ></FormButtons>
                  </div>
                </div>
              </form>
            </FormProvider>
          )}
        </LoadingContentWrapper>
      </div>
    </>
  );
}

